/**
 * Reactive-variable store for the add-players panel's expand requests (SE-6488).
 *
 * The SE-5490 add-players panel is the board's LEFT RAIL, not a modal — it owns
 * its own collapsed state and starts collapsed on a board that already has
 * cards. The board header's `+ Add player` primary CTA has to be able to expand
 * it from the outside. Rather than prop-drill a callback through
 * MainView -> AddPlayersPanel, this follows the same `makeVar` convention as
 * `stageEditorStore` / `rulesPanelStore` so the toolbar can drive the rail
 * directly.
 *
 * The state is an incrementing REQUEST COUNTER, not an `open` boolean. The
 * panel consumes it as part of its key-reset key, so each press of
 * `+ Add player` remounts the rail expanded — the repo's sanctioned alternative
 * to a reset-on-prop-change effect (`react-hooks/set-state-in-effect`, the
 * SE-5524 ConfirmDialog precedent). A boolean would need a matching
 * "close" write from inside the panel to be re-openable, which is exactly the
 * two-writer coupling the counter avoids.
 *
 * The request carries the board it was made for so the expansion does NOT stick
 * across a pipeline switch: opening the rail on pipeline A must not force it
 * open when the user later navigates to pipeline B, whose own card-count
 * default should win again.
 * @module features/player-kanban/stores/addPlayersPanelStore
 */

import { makeVar } from "@apollo/client";

/** A request from some surface to expand the add-players rail. */
export interface AddPlayersExpandRequest {
  /**
   * Monotonic counter. Bumped on every press so a second press re-expands a
   * rail the user has since collapsed. `0` means "never asked".
   */
  readonly requestId: number;
  /** The board the request was made for; scopes the expansion to that pipeline. */
  readonly boardId: string | null;
}

/** Initial (no request) state. */
const INITIAL_STATE: AddPlayersExpandRequest = {
  requestId: 0,
  boardId: null,
};

/** Apollo reactive variable holding the latest expand request. */
export const addPlayersExpandRequestVar =
  makeVar<AddPlayersExpandRequest>(INITIAL_STATE);

/**
 * Ask the add-players rail to expand for one board.
 * @param boardId - The pipeline whose rail should expand.
 */
export function requestAddPlayersExpand(boardId: string | null): void {
  addPlayersExpandRequestVar({
    requestId: addPlayersExpandRequestVar().requestId + 1,
    boardId,
  });
}

/** Reset to the no-request state. Test-support seam; not used in app code. */
export function resetAddPlayersExpandRequests(): void {
  addPlayersExpandRequestVar(INITIAL_STATE);
}
